import type { PropsWithChildren } from "react";
import { useEffect, useMemo, useRef } from "react";
import type { StyleProp, ViewStyle } from "react-native";
import { Redirect, usePathname } from "expo-router";
import { Layout as BaseLayout } from "@ptsd/uikit";
import type { ScrollViewProps } from "tamagui";
import { ScrollView, useMedia, View, YStack } from "tamagui";

import { api } from "~/utils/api";
import { ChatBotDialog } from "~/component/feature/chat-bot";
import { ResultNavbar } from "../feature";
import { Footer, FooterAuth } from "./footer";
import { Header, HeaderAuth } from "./header";

export const useLayoutScrollProps = (maxWidth = 952): ScrollViewProps => {
  return useMemo(() => {
    return {
      style: { scrollbarGutter: "stable" } as StyleProp<ViewStyle>,
      stickyHeaderIndices: [0],
      contentContainerStyle: {
        width: "100%",
        minHeight: "100dvh",
        alignSelf: "center",
        maxWidth,
      },
    };
  }, [maxWidth]);
};

export const MainLayout: React.FC<
  PropsWithChildren & { hideFooter?: boolean }
> = ({ children, hideFooter = false }) => {
  const scrollProps = useLayoutScrollProps();

  return (
    <BaseLayout scrollProps={scrollProps}>
      <Header />
      <YStack
        flexGrow={1}
        gap={"$3"}
        paddingTop={"$3"}
        paddingBottom={"$6"}
        width={"100%"}
        alignSelf="center"
        alignItems="center"
      >
        {children}
      </YStack>
      {!hideFooter && (
        <Footer
          $sm={{ paddingHorizontal: "$0" }}
          paddingHorizontal={"$0"}
          paddingBottom={"$3"}
          alignSelf="center"
        />
      )}
      <ChatBotDialog />
    </BaseLayout>
  );
};

export const SurveyLayout: React.FC<PropsWithChildren> = ({ children }) => {
  const { sm } = useMedia();
  return (
    <BaseLayout variant="dualbackground">
      <View
        minHeight={"100dvh"}
        minWidth={"100vw"}
        flexDirection="row"
        justifyContent="space-between"
      >
        {!sm && (
          <View backgroundColor={"white"} minHeight={"100dvh"} width={73} />
        )}
        {children}
        {!sm && (
          <View minHeight={"100dvh"} backgroundColor={"white"} width={73} />
        )}
      </View>
    </BaseLayout>
  );
};

const StickyHeaderWrapper: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <YStack
      backgroundColor={"$white"}
      borderRadius={0}
      borderBottomWidth={"$0.1"}
      borderBottomColor={"$gray-100"}
      zIndex={"$2"}
      $sm={{ top: 58 }}
      $gtSm={{ top: 84 }}
      $platform-web={{ position: "sticky" }}
    >
      {children}
    </YStack>
  );
};

export const ResultsLayout: React.FC<PropsWithChildren> = ({ children }) => {
  const { sm } = useMedia();

  const ref = useRef<ScrollView>(null);
  const pathname = usePathname();

  useEffect(() => {
    ref.current?.scrollTo({ y: 0, animated: false });
  }, [pathname]);

  const scrollProps = useLayoutScrollProps();

  if (sm) {
    return (
      <BaseLayout>
        <ScrollView ref={ref} height="100vh" {...scrollProps}>
          <Header />
          <StickyHeaderWrapper>
            <ScrollView
              horizontal
              paddingHorizontal={"$2"}
              showsHorizontalScrollIndicator={false}
            >
              <ResultNavbar />
            </ScrollView>
          </StickyHeaderWrapper>
          {children}
          <Footer />
        </ScrollView>
        <ChatBotDialog />
      </BaseLayout>
    );
  }

  return (
    <BaseLayout>
      <ScrollView ref={ref} height="100vh" {...scrollProps}>
        <Header />
        <StickyHeaderWrapper>
          <ResultNavbar />
        </StickyHeaderWrapper>
        <View
          gap={"$3"}
          flexGrow={1}
          paddingTop={"$3"}
          paddingBottom={"$6"}
          alignSelf="center"
          width={"100%"}
        >
          {children}
          <Footer />
        </View>
      </ScrollView>
      <ChatBotDialog />
    </BaseLayout>
  );
};

export const AuthLayout: React.FC<PropsWithChildren> = ({ children }) => {
  const [person] = api.context.person.useSuspenseQuery(undefined);
  const scrollProps = useLayoutScrollProps(1248);

  if (person) return <Redirect href={"/"} />;

  return (
    <BaseLayout variant="dualbackground" scrollProps={scrollProps}>
      <View />
      <View flexGrow={1}>
        <HeaderAuth />
        {children}
      </View>
      <FooterAuth />
    </BaseLayout>
  );
};
